import { useEffect, useMemo } from "react";
import { validatePhoneNumberLength } from "libphonenumber-js";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiButton,
  MiDetailFields,
  MiLoader,
  MiModalConfirm,
} from "@miplatform/components";
import {
  ACCOUNT_TYPE,
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  CONTACT_CARD_EDIT_MODES,
  INPUT_TYPES,
  MODAL_TITLES,
  PERMISSIONS,
  PREFERRED_CONTACT_METHOD,
  PREFERRED_CONTACT_METHOD_OPTIONS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { capitalize, getPhoneNumber, isEmpty } from "@miplatform/utils";
import { useMiContactEditViewModel } from "@miplatform/view-models";
import { mdiTrashCanOutline } from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Divider, Typography } from "@mui/material";
import { Box } from "@mui/system";

const DEFAULT_CONTACT = {
  accountId: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  title: "",
  preferredContactMethod: PREFERRED_CONTACT_METHOD.Phone,
  label: "",
  secondaryPhone: "",
  isPrimary: false,
  isVip: false,
};

/**
 * Implementation for a global edit component for contacts.
 *
 * @param {Object} props - The component main prop object.
 * @param {boolean} props.isLoading - Indicates if the parent component is loading.
 * @param {boolean} props.isPrimaryDisabled - Disables the option to mark relationships as primary if true.
 * @param {Object} props.contact - Object containing the contact information.
 * @param {Function} props.onClose - Function to close the edit mode.
 * @param {string} props.editMode - Set the edit mode for the contact card. Possible values are defined in the `CONTACT_CARD_EDIT_MODES` constant.
 * @param {string} props.currentEditMode - The current edit mode (e.g., `CONTACT_CARD_EDIT_MODES.CONTACT` or `CONTACT_CARD_EDIT_MODES.RELATIONSHIP`).
 * @param {Object} [props.referenceIds={}] - Mapping of contact levels to their corresponding reference IDs.
 * @param {string} props.defaultContactLevel - The default contact level for new relationships.
 * @param {Object} [props.contactCategories={}] - Mapping of contact levels to their corresponding categories and options.
 * @param {Function} props.onRefresh - Function to refresh data after creating, updating, or deleting relationships.
 *
 * @returns {JSX.Element} The rendered contact edit component.
 *
 * @example
 * <MiContactEdit
 *   isLoading={isLoading}
 *   contact={contactData}
 *   onClose={handleClose}
 *   editMode={CONTACT_CARD_EDIT_MODES.CONTACT}
 *   currentEditMode={CONTACT_CARD_EDIT_MODES.CONTACT}
 *   referenceIds={referenceIdsData}
 *   defaultContactLevel={CONTACT_TYPES.COMMUNITIES}
 *   contactCategories={contactCategoriesData}
 *   isContactLevelReadOnly={true}
 *   onRefresh={handleRefresh}
 * />
 */
export const MiContactEdit = ({
  isLoading: isCardLoading,
  isPrimaryDisabled,
  accountIds = null,
  accountTypes = null,
  defaultAccountId = null,
  contact,
  onClose,
  editMode,
  currentEditMode,
  referenceIds = {},
  defaultContactLevel,
  contactCategories = {},
  onRefresh,
}) => {
  const { theme, classes } = useStyle();
  const { hasPermission } = usePermissions();
  const edit = useEdit({
    ...DEFAULT_CONTACT,
    ...contact,
  });
  const relationshipEdit = useEdit(
    {
      level: defaultContactLevel,
      ...DEFAULT_CONTACT,
      ...contact,
    },
    [
      {
        name: "label",
        validationRule: z.string().min(1, "Relationship Type is required"),
      },
    ]
  );

  const resetEdit = () => {
    edit.update({ ...DEFAULT_CONTACT });
  };

  const {
    isLoading,
    isDeleteModalOpen,
    isRelationshipMode,
    isContactMode,
    isCreateMode,
    accounts,
    handleSubmit,
    isFormValid,
    getAccounts,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    deleteRelationship,
  } = useMiContactEditViewModel({
    accountIds,
    accountTypes,
    contact,
    defaultContactLevel,
    referenceIds,
    onRefresh,
    onClose,
    editMode,
    currentEditMode,
    edit,
    relationshipEdit,
  });

  const selectedAccount = useMemo(
    () => accounts.find((account) => account.id === edit.getValue("accountId")),
    [accounts, edit.edits.accountId]
  );

  const isEmailRequired =
    selectedAccount?.accountType !== ACCOUNT_TYPE.SERVICE ||
    edit.getValue("preferredContactMethod") === PREFERRED_CONTACT_METHOD.Email;

  const handlePhoneChange = (e, field) => {
    if (
      validatePhoneNumberLength(getPhoneNumber(e.target.value), "US") ===
      "TOO_LONG"
    ) {
      return;
    }

    edit.update({
      [field]: getPhoneNumber(e.target.value),
    });

    if (field === "secondaryPhone" && e.target.value === "") {
      edit.deleteError(field);
    }
  };

  const contactFields = [
    {
      label: "Account",
      name: "accountId",
      fieldType: INPUT_TYPES.SELECT,
      required: isCreateMode,
      options: accounts,
      renderOption: (option) => (
        <Box className={classes.menuItem}>
          <Typography className={classes.option}>{option.label}</Typography>
          <Typography>{option.accountType}</Typography>
        </Box>
      ),

      width: "50%",
      style: { ...(!isCreateMode && { display: "none" }) },
      validationRule: isCreateMode
        ? z.string().min(1, "Account is required")
        : null,
    },
    {
      label: "First Name",
      name: "firstName",
      required: true,
      width: "50%",
      readOnly: !isContactMode,
      beforeOnChange: capitalize,
      validationRule: z.string().min(1, "First name is required"),
    },
    {
      label: "Last Name",
      name: "lastName",
      required: true,
      width: "50%",
      readOnly: !isContactMode,
      beforeOnChange: capitalize,
      validationRule: z.string().min(1, "Last name is required"),
    },
    {
      label: "Phone Number",
      name: "phone",
      value: getPhoneNumber(edit.getValue("phone")),
      required: true,
      width: "50%",
      readOnly: !isContactMode,
      handleChange: (e) => handlePhoneChange(e, "phone"),
      validationRule: z
        .string()
        .min(1, "Phone number is required")
        .refine(
          (value) => validatePhoneNumberLength(value, "US") === undefined,
          {
            message: "Please enter a valid phone number",
          }
        ),
    },
    {
      label: "Secondary Phone Number",
      name: "secondaryPhone",
      value: getPhoneNumber(edit.getValue("secondaryPhone")),
      width: "50%",
      readOnly: !isContactMode,
      handleChange: (e) => handlePhoneChange(e, "secondaryPhone"),
      validationRule: edit.getValue("secondaryPhone")
        ? z
            .string()
            .refine(
              (value) =>
                value && validatePhoneNumberLength(value, "US") === undefined,
              {
                message: "Please enter a valid phone number",
              }
            )
        : null,
    },
    {
      label: "Email",
      name: "email",
      required: isEmailRequired,
      width: "50%",
      readOnly: !isContactMode,
      validationRule: isEmailRequired
        ? z
            .string()
            .min(1, "Email is required")
            .email("Please enter a valid email address")
        : null,
    },
    {
      label: "Preferred Method",
      name: "preferredContactMethod",
      width: "50%",
      fieldType: INPUT_TYPES.SELECT,
      options: PREFERRED_CONTACT_METHOD_OPTIONS,
      readOnly: !isContactMode,
      validationRule: z.string().min(1, "Preferred contact method is required"),
    },
    {
      label: "VIP",
      name: "isVip",
      fieldType: INPUT_TYPES.CHECKBOX,
      readOnly: isRelationshipMode,
      validationRule: z.boolean().optional(),
      width: "50%",
    },
  ];

  const relationshipFields = [
    {
      label: "Relationship Type",
      name: "label",
      fieldType: "select",
      options:
        contactCategories[relationshipEdit.getValue("level")]?.options || [],
      required: true,
      handleChange: (e) =>
        relationshipEdit.update({
          isPrimary: false,
          label: e.target.value,
        }),
    },
    {
      label: "Primary",
      name: "isPrimary",
      fieldType: "checkbox",
      disabled:
        isPrimaryDisabled || isEmpty(relationshipEdit.getValue("label")),
      readOnly: !contactCategories[
        relationshipEdit.getValue("level")
      ]?.primaryRelationshipTypes.has(relationshipEdit.getValue("label")),
    },
  ];

  edit.setValidation(contactFields);

  const handleCancel = () => {
    resetEdit();
    onClose();
  };

  useEffect(() => {
    if (isCreateMode) {
      getAccounts();

      if (defaultAccountId) {
        edit.update({ accountId: defaultAccountId });
      }
    }
  }, [isCreateMode, defaultAccountId]);

  useEffect(() => {
    edit.deleteError("email");
  }, [selectedAccount]);

  return (
    <Box className={classes.container}>
      <MiLoader
        type="linear"
        isLoading={isLoading}
        style={{ position: "absolute", top: 0, left: 0 }}
      />
      <Box className={classes.fieldsContainer}>
        {isContactMode && (
          <MiDetailFields detailFields={contactFields} edit={edit} />
        )}
        {isRelationshipMode && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Divider sx={{ mt: 2 }} />
            <MiDetailFields
              detailFields={relationshipFields}
              edit={relationshipEdit}
            />
          </Box>
        )}
        <Box className={classes.buttonContainer}>
          <MiButton
            color={BUTTON_COLORS.BLACK}
            title="Cancel"
            variant="text"
            onClick={handleCancel}
          />
          {contact?.relatedReferenceId &&
            currentEditMode === CONTACT_CARD_EDIT_MODES.RELATIONSHIP &&
            hasPermission(PERMISSIONS.CONTACT.DELETE) && (
              <MiButton
                title="Remove"
                variant={BUTTON_VARIANTS.CONTAINED}
                color={BUTTON_COLORS.RED}
                disabled={isLoading || isCardLoading}
                onClick={handleOpenDeleteModal}
              />
            )}
          <MiButton
            title={BUTTON_LABELS.SUBMIT}
            variant={BUTTON_VARIANTS.CONTAINED}
            disabled={!isFormValid() || isLoading || isCardLoading}
            onClick={handleSubmit}
          />
        </Box>
        <MiModalConfirm
          title={MODAL_TITLES.DELETE}
          titleIcon={{
            path: mdiTrashCanOutline,
            color: theme.palette.error.main,
          }}
          description="Are you sure you want to delete this relationship?"
          open={isDeleteModalOpen}
          handleNegative={handleCloseDeleteModal}
          handlePositive={deleteRelationship}
        />
      </Box>
    </Box>
  );
};

const useStyle = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
  },
  fieldsContainer: {
    display: "flex",
    flexDirection: "column",
    alignContent: "space-around",
    width: "100%",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    whiteSpace: "nowrap",
    overflow: "hidden",
    width: "100%",
    height: 23,
    color: theme.palette.bluegrey.main,
  },
  option: {
    fontWeight: theme.fontWeight.bold,
    lineHeight: 2,
    color: theme.palette.common.black,
  },
}));
